import React, { useRef, useEffect, useCallback } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components, GroupBase, OptionsOrGroups } from 'react-select';
import { useField } from '@unform/core';

import { ChevronDownIcon } from '../../../assets/svg/SVGComponents';
import { Container, InputError } from './styles';

interface OptionType {
  label: string;
  value: string | number;
}

interface Props {
  name: string;
  loadOptions: (
    inputValue: string,
  ) => Promise<OptionsOrGroups<OptionType, GroupBase<OptionType>>>;
  dropdownIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  iconLeft?: React.FC<React.SVGProps<SVGSVGElement>>;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  onCreateOption?: (inputValue: string) => void;
  defaultOptions?: OptionsOrGroups<OptionType, GroupBase<OptionType>> | boolean;
  isClearable?: boolean;
  isMulti?: boolean;
  createOptionThreshold?: number; // Mínimo de caracteres para exibir "Criar opção"
  [key: string]: any; // Para outras props do react-select
}

const AsyncCreateSelect: React.FC<Props> = ({
  name,
  dropdownIcon: Icon,
  iconLeft: IconLeft,
  formatCreateLabel,
  onCreateOption,
  loadOptions,
  createOptionThreshold = 3, // Por padrão, exibe "Criar opção" após 3 caracteres
  ...rest
}) => {
  const selectRef = useRef<any>(null);
  const { fieldName, defaultValue, registerField, error } = useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      getValue: ref => {
        if (rest.isMulti) {
          if (!ref.state.selectValue) {
            return [];
          }

          return ref.state.selectValue.map(
            (option: OptionType) => option.value,
          );
        }
        if (!ref.state.selectValue || ref.state.selectValue.length === 0) {
          return '';
        }

        return ref.state.selectValue[0]?.value;
      },
      setValue: (ref, value) => {
        if (ref) {
          ref.setValue(value);
        }
      },
      clearValue: ref => {
        if (ref) {
          ref.clearValue();
        }
      },
    });
  }, [fieldName, registerField, rest.isMulti]);

  const defaultFormatCreateLabel = useCallback((inputValue: string) => {
    return `Criar "${inputValue}"`;
  }, []);

  // Função para processar loadOptions e garantir que opções só sejam
  // carregadas quando o usuário digitar
  const processLoadOptions = useCallback(
    async (
      inputValue: string,
    ): Promise<OptionsOrGroups<OptionType, GroupBase<OptionType>>> => {
      // Se o texto pesquisado for menor que 3 caracteres, não busca
      if (!inputValue || inputValue.length < 3) {
        return [];
      }

      // Chama a função loadOptions passada por props
      return loadOptions(inputValue);
    },
    [loadOptions],
  );

  const DropdownIndicator = useCallback(
    (props: any) => {
      return (
        <components.DropdownIndicator {...props}>
          {Icon ? (
            <Icon width="1.22rem" fill="none" strokeWidth={2} />
          ) : (
            <ChevronDownIcon width="1.22rem" strokeWidth={2} />
          )}
        </components.DropdownIndicator>
      );
    },
    [Icon],
  );

  const Control = useCallback(
    (props: any) => {
      return (
        <components.Control {...props}>
          {IconLeft && (
            <IconLeft
              width="1.33rem"
              stroke="#323232"
              fill="none"
              strokeWidth={2}
            />
          )}
          {props.children}
        </components.Control>
      );
    },
    [IconLeft],
  );

  // Verifica se a opção de criação deve ser exibida
  const shouldShowCreateOption = useCallback(
    (inputValue: string) => {
      return inputValue.length >= createOptionThreshold;
    },
    [createOptionThreshold],
  );

  // Mensagem personalizada para quando não há opções
  const noOptionsMessage = useCallback(
    ({ inputValue }: { inputValue: string }) => {
      if (!inputValue || inputValue.length < 3) {
        return 'Digite pelo menos 3 caracteres para buscar';
      }
      return 'Nenhuma opção encontrada';
    },
    [],
  );

  return (
    <Container>
      <AsyncCreatableSelect
        cacheOptions
        defaultValue={defaultValue}
        ref={selectRef}
        classNamePrefix="react-select"
        loadOptions={processLoadOptions}
        formatCreateLabel={formatCreateLabel || defaultFormatCreateLabel}
        onCreateOption={onCreateOption}
        noOptionsMessage={noOptionsMessage}
        components={{ DropdownIndicator, Control }}
        defaultOptions={false} // Não carrega opções inicialmente
        isValidNewOption={(inputValue, _, options) => {
          // Só permite criar se tiver texto suficiente e não existir nas opções
          return (
            shouldShowCreateOption(inputValue) &&
            !(options as readonly OptionType[]).some(
              option => option.label.toLowerCase() === inputValue.toLowerCase(),
            )
          );
        }}
        {...rest}
      />
      {error && <InputError>{error}</InputError>}
    </Container>
  );
};

export default AsyncCreateSelect;
